import {useContext, useMemo} from 'react';

import {AccountType} from '../../../components/Search/SearchNav/types';
import {envIsPublicCloud} from '../../../config';
import {
  useFetchArtifactCollectionSupplementalQuery,
  useFetchRegistryProjectsPartialQuery,
  useOrganizationReadOnlyQuery,
  useProjectReadOnlyQuery,
  useRegistryProjectQuery,
} from '../../../generated/graphql';
import {DEFAULT_ONPREM_ORGANIZATION_NAME} from '../../../pages/OrgDashboard/OrgRoles/RolesDashboard';
import {useViewer} from '../../../state/viewer/hooks';
import {propagateErrorsContext} from '../../../util/errors';
import {useStatsigGateSourceArtifactProtection} from '../../../util/rampFeatureFlags';
import {AccountSelectorContext} from '../../Search/SearchNav/AccountSelectorContextProvider';
import {getGlobalRegistryType, REGISTRY_PROJECT_PREFIX} from './utils';

// Keeping this hook here till we deprecate the model registry for personal entities on SaaS and Server.
export const useIsGlobalRegistryEnabled = (
  entityName?: string,
  isEntityNameLoading?: boolean
): {
  enableGlobalRegistry: boolean;
  enableGlobalRegistryLoading: boolean;
} => {
  const {selectedAccount} = useContext(AccountSelectorContext);

  const isPersonalAccountOnSaaS =
    selectedAccount?.accountType === AccountType.Personal && envIsPublicCloud;
  const viewer = useViewer();
  const isPersonalEntity =
    entityName != null && viewer?.username === entityName;
  return useMemo(() => {
    if (isPersonalAccountOnSaaS || isPersonalEntity) {
      return {
        enableGlobalRegistry: false,
        enableGlobalRegistryLoading: isEntityNameLoading ?? false,
      };
    }
    return {
      enableGlobalRegistry: true,
      enableGlobalRegistryLoading: false,
    };
  }, [isPersonalEntity, isEntityNameLoading, isPersonalAccountOnSaaS]);
};

export const useIsOrgReadOnly = (): {
  isOrganizationReadOnly: boolean;
  orgId: string;
  entityName: string;
  loading: boolean;
} => {
  const {selectedAccount} = useContext(AccountSelectorContext);
  const orgName = envIsPublicCloud
    ? (selectedAccount?.name ?? '')
    : DEFAULT_ONPREM_ORGANIZATION_NAME;
  const {data: organizationData, loading: organizationLoading} =
    useOrganizationReadOnlyQuery({
      variables: {orgName},
      context: propagateErrorsContext(),
    });

  const state = useMemo(
    () => ({
      isOrganizationReadOnly:
        organizationData?.organization?.orgEntity?.readOnly ?? true,
      orgId: organizationData?.organization?.id ?? '',
      entityName: organizationData?.organization?.orgEntity?.name ?? '',
      loading: organizationLoading,
    }),
    [organizationData, organizationLoading]
  );
  return state;
};

export const useIsProjectReadOnly = (
  entityName: string,
  projectName: string
): {isProjectReadOnly: boolean; loading: boolean} => {
  const {data, loading} = useProjectReadOnlyQuery({
    variables: {entityName, projectName},
    context: propagateErrorsContext(),
    skip: entityName === '' || projectName === '',
  });

  const state = useMemo(
    () => ({
      isProjectReadOnly: data?.entity?.project?.readOnly ?? true,
      loading,
    }),
    [data, loading]
  );
  return state;
};

export const useRegistryProject = (orgName: string, projectName?: string) => {
  const fullProjectName = `${REGISTRY_PROJECT_PREFIX}${projectName}`;

  const {data, loading, error} = useRegistryProjectQuery({
    variables: {orgName, projectName: fullProjectName},
    context: propagateErrorsContext(),
    skip: !orgName || !projectName,
  });

  const registryProject = useMemo(() => {
    const entityName = data?.organization?.orgEntity?.name ?? '';
    const orgId = data?.organization?.id ?? '';
    if (data?.organization?.orgEntity?.project != null && projectName != null) {
      return getGlobalRegistryType(
        data?.organization?.orgEntity?.project,
        projectName,
        entityName,
        orgId,
        orgName
      );
    }
    return undefined;
  }, [data, projectName, orgName]);

  return {
    data: registryProject,
    loading,
    error,
  };
};

export const useFetchArtifactCollectionSupplemental = (
  collectionId: string
) => {
  const {data, loading, error} = useFetchArtifactCollectionSupplementalQuery({
    variables: {collectionId},
    context: propagateErrorsContext(),
  });

  const numConsumers = useMemo(() => {
    const consumers = data?.artifactCollection?.artifacts?.edges.flatMap(edge =>
      edge.node.usedBy.edges.map(usedBy => usedBy.node.user?.name)
    );
    return new Set(consumers).size;
  }, [data]);

  return {
    isProjectDeleted: !data?.artifactCollection?.project,
    numVersions: data?.artifactCollection?.artifacts?.totalCount ?? 0,
    numConsumers,
    loading,
    error,
  };
};

export const useIsSourceArtifactProtectionEnabled = () => {
  const {selectedAccount} = useContext(AccountSelectorContext);
  const orgName = envIsPublicCloud
    ? (selectedAccount?.name ?? '')
    : DEFAULT_ONPREM_ORGANIZATION_NAME;
  return useStatsigGateSourceArtifactProtection(orgName);
};

export const useRegistrySetup = () => {
  const {selectedAccount} = useContext(AccountSelectorContext);
  const orgName = envIsPublicCloud
    ? (selectedAccount?.name ?? '')
    : DEFAULT_ONPREM_ORGANIZATION_NAME;

  // Initial fetch to setup default registries if they don't exist
  const {data: registries, loading: isRegistriesLoading} =
    useFetchRegistryProjectsPartialQuery({
      variables: {
        orgName,
        limit: 100,
      },
    });

  return {
    orgName,
    registries,
    isRegistriesLoading,
  };
};
